import React from "react";
import { BrandWordmark } from "./components/BrandArtwork";

export const LEGAL_VERSION = "2026-08-21";
export const SMS_DISCLOSURE_VERSION = "sms-consent-2026-06-25";
export const LEGAL_CONTACT_EMAIL = "support@chara.run";

const policySections = [
  {
    title: "Overview",
    body: [
      "Chara is a beta running-coaching service that uses athlete profile data, workout history, messaging, and large language models to help generate coaching guidance, training plans, adaptations, and reminders.",
      "This Privacy Policy explains what we collect, how we use it, and the choices you have. Chara is not a medical provider and is not a substitute for professional medical advice, diagnosis, treatment, physical therapy, emergency care, or a licensed coach's in-person judgment.",
    ],
  },
  {
    title: "Information We Collect",
    body: [
      "Account and contact information, including name, email, phone number, Auth0 identity, time zone, preferred distance, plan tier, role, and messaging preferences.",
      "Athlete and coaching information, including training background, availability, training preferences, PRs, max mileage, current fitness estimates, stimulus profiles, injuries, soreness, constraints, coaching intents, soft memory, workout feedback, adaptation reviews, workout plans, manual workout entries, completed workouts, heart-rate metrics, effort, difficulty, training stress, and athlete notes.",
      "Messages and communications, including inbound and outbound text or chat content, message direction, timestamps, conversation state, Telegram or SMS endpoint metadata, pending SMS state, feedback records, support messages, and opt-in or consent metadata.",
      "Integration information, including workout export settings, linked messaging accounts, and delivery destinations.",
      "Operational information, including prompt runs, model provider and model metadata, prompt messages, raw model responses, token counts, cost estimates, trace events, errors, usage counters, analytics, waitlist entries, and device/browser information provided by your browser or service providers.",
    ],
  },
  {
    title: "How We Use Information",
    body: [
      "To create and maintain your account, athlete profile, training plans, workout adaptations, coaching memory, messaging endpoints, and integrations.",
      "To generate, personalize, audit, and improve coaching responses using LLM providers and related infrastructure.",
      "To send service messages, account notices, coaching reminders, support replies, safety notices, and beta access communications where permitted.",
      "To monitor reliability, abuse, security, costs, product quality, and compliance with applicable laws and carrier messaging rules.",
    ],
  },
  {
    title: "AI and Health-Related Coaching",
    body: [
      "Chara may process health-adjacent fitness information such as injury history, soreness, fatigue, recovery, heart-rate data, training stress, and notes you provide. This information is used for coaching context and risk-aware training guidance.",
      "LLM outputs can be incomplete, incorrect, or inappropriate for your situation. You are responsible for deciding whether to follow any recommendation. Stop activity and seek medical attention if you experience severe pain, chest pain, faintness, shortness of breath, neurological symptoms, suspected fracture, heat illness, or any other urgent or concerning symptom.",
      "Do not use Chara to request diagnosis or treatment. Consult a qualified clinician before starting or changing training if you are injured, pregnant, managing a medical condition, returning from illness, or uncertain about your safety.",
    ],
  },
  {
    title: "Sharing and Service Providers",
    body: [
      "We may share information with vendors that help operate Chara, including cloud hosting, databases, authentication, messaging providers such as Twilio or Telegram, LLM providers, analytics, observability, and connected fitness services you authorize.",
      "We may disclose information if required by law, to protect rights and safety, to investigate abuse, or in connection with a business transaction such as merger, financing, or sale of assets.",
      "We do not sell personal information. Mobile opt-in information and consent data will not be sold, rented, or shared with third parties for their marketing or promotional purposes.",
    ],
  },
  {
    title: "Messaging Choices",
    body: [
      "If you opt in to SMS, Chara may send recurring coaching, account, onboarding, and support messages. Message frequency varies. Message and data rates may apply.",
      "You can opt out of SMS by replying STOP. You can request help by replying HELP or contacting us. Opting out of SMS does not necessarily close your account, but it may limit text-first coaching features.",
    ],
  },
  {
    title: "Retention, Security, and Your Choices",
    body: [
      "We retain information for as long as reasonably needed to provide Chara, maintain coaching history, comply with legal obligations, resolve disputes, enforce agreements, improve reliability, and protect the service.",
      "We use administrative, technical, and organizational safeguards designed to protect information, but no internet service is perfectly secure.",
      "You may request access, correction, deletion, or export of your information by contacting us. Some records may be retained where required for security, legal, audit, backup, fraud prevention, or carrier compliance reasons.",
    ],
  },
  {
    title: "Children and International Users",
    body: [
      "Chara is intended for users who are at least 18 years old. Do not use Chara if you are under 18.",
      "Chara is operated from the United States. If you access it from another region, you understand that your information may be processed in the United States and other locations where our providers operate.",
    ],
  },
];

const termsSections = [
  {
    title: "Agreement to Terms",
    body: [
      "These Terms and Conditions govern your access to and use of Chara. By creating an account, joining onboarding, connecting messaging, or using Chara, you agree to these Terms and the Privacy Policy.",
      "If you do not agree, do not use Chara.",
    ],
  },
  {
    title: "Beta Service",
    body: [
      "Chara is currently a beta product. Features may change, fail, be rate limited, or be discontinued. We may suspend or restrict access for safety, security, abuse prevention, operational, or compliance reasons.",
      "You are responsible for providing accurate information and keeping your account credentials secure.",
    ],
  },
  {
    title: "No Medical Advice",
    body: [
      "Chara provides fitness and running-coaching information only. Chara does not provide medical advice, diagnosis, treatment, physical therapy, emergency services, or professional healthcare.",
      "Training involves risk, including injury, illness, and death. You are responsible for your training decisions. Consult qualified professionals before changing training if you have injuries, symptoms, medical conditions, or safety concerns.",
      "Do not ignore professional medical advice because of anything Chara says. Stop training and seek appropriate care for urgent or concerning symptoms.",
    ],
  },
  {
    title: "AI Outputs and Coaching Decisions",
    body: [
      "Chara uses LLMs and automation. Outputs may be inaccurate, incomplete, delayed, or unsuitable for your circumstances. You should independently evaluate any plan, workout, adaptation, or message before acting on it.",
      "You agree not to rely on Chara as your only source of judgment for health, safety, race-day, injury, or medical decisions.",
    ],
  },
  {
    title: "Messaging Terms",
    body: [
      "Chara may offer recurring SMS or chat-based coaching, onboarding, support, account, and service messages. Message frequency varies based on your use, account status, workouts, reminders, and support needs.",
      "Message and data rates may apply. Reply STOP to opt out of SMS. Reply HELP for help. You may also contact Chara at support@chara.run.",
      "Carriers are not liable for delayed or undelivered messages. Consent to receive SMS is not required as a condition of purchase or account creation unless SMS is necessary for a specific feature you choose to use.",
      "See the Privacy Policy for how messaging data and mobile opt-in information are handled.",
    ],
  },
  {
    title: "Acceptable Use",
    body: [
      "Do not misuse Chara, attempt unauthorized access, interfere with infrastructure, submit unlawful content, harass others, reverse engineer protected systems, abuse messaging channels, or use Chara for emergency services.",
      "Do not submit payment card numbers, government identifiers, credentials, or other sensitive secrets through coaching messages.",
    ],
  },
  {
    title: "Integrations and Third Parties",
    body: [
      "If you connect third-party services such as Telegram, SMS, or workout export destinations, their terms and privacy policies also apply. You are responsible for the permissions you grant to those services.",
      "We are not responsible for third-party services, carrier availability, message filtering, device compatibility, or delays outside our control.",
    ],
  },
  {
    title: "Disclaimers and Limits",
    body: [
      "Chara is provided as is and as available, without warranties of any kind to the fullest extent permitted by law.",
      "To the fullest extent permitted by law, Chara and its operators will not be liable for indirect, incidental, special, consequential, punitive, or exemplary damages, or for lost profits, data, goodwill, training outcomes, race outcomes, or injury-related losses.",
    ],
  },
  {
    title: "Changes and Contact",
    body: [
      "We may update these Terms. Continued use after changes means you accept the updated Terms.",
      "Questions can be sent to support@chara.run.",
    ],
  },
];

function LegalChrome({ eyebrow, title, intro, updated = LEGAL_VERSION, children }) {
  return (
    <main className="legal-shell">
      <nav className="legal-topbar" aria-label="Legal navigation">
        <a href="/" className="legal-brand-link" aria-label="Chara home"><BrandWordmark compact subtitle="" /></a>
        <div className="legal-nav-links">
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="/onboarding">Join beta</a>
        </div>
      </nav>
      <section className="legal-hero">
        <div>
          <div className="section-kicker">{eyebrow}</div>
          <h1>{title}</h1>
          <p>{intro}</p>
          <span>Last updated {updated}</span>
        </div>
      </section>
      {children}
      <LegalFooter />
    </main>
  );
}

function LegalFooter() {
  return (
    <footer className="legal-footer">
      <span>Chara legal</span>
      <a href="/privacy">Privacy Policy</a>
      <a href="/terms">Terms and Conditions</a>
      <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a>
    </footer>
  );
}

function SectionList({ sections }) {
  return (
    <div className="legal-document">
      {sections.map((section) => (
        <section className="legal-section" key={section.title}>
          <h2>{section.title}</h2>
          {section.body.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        </section>
      ))}
    </div>
  );
}

export function PrivacyPolicyPage() {
  return (
    <LegalChrome
      eyebrow="Privacy Policy"
      title="How Chara handles athlete, messaging, and coaching data."
      intro="This policy is written for a beta running-coaching product that stores training context, uses LLMs, and supports text-first coaching."
    >
      <SectionList sections={policySections} />
    </LegalChrome>
  );
}

export function TermsPage() {
  return (
    <LegalChrome
      eyebrow="Terms and Conditions"
      title="The rules for using Chara safely."
      intro="These terms set expectations for beta access, AI coaching, messaging, injury-related context, and your responsibility for training decisions."
    >
      <SectionList sections={termsSections} />
    </LegalChrome>
  );
}

export function SmsConsentProofPage() {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://example.com";
  return (
    <LegalChrome
      eyebrow="SMS Opt-In Proof"
      title="Chara SMS consent example."
      intro="This public page documents the complete, branded opt-in flow and disclosures used for carrier and Twilio toll-free messaging review."
      updated="2026-06-27"
    >
      <section className="legal-proof-grid">
        <div className="legal-proof-card primary">
          <div className="section-kicker">Program</div>
          <h2>Chara text coaching and account messages</h2>
          <p>
            Chara is a running-coaching service. Athletes can opt in during Chara beta onboarding by entering their mobile phone number and checking a separate SMS consent box. The checkbox is optional and is not preselected.
          </p>
          <p>
            The opt-in disclosure tells athletes that Chara may send recurring running-plan messages, workout reminders, coaching follow-ups, schedule updates, onboarding messages, account updates, and support replies; that message frequency varies; that message and data rates may apply; and that they can reply STOP to opt out or HELP for help.
          </p>
        </div>
        <div className="legal-proof-card">
          <div className="section-kicker">Public URLs</div>
          <dl className="legal-proof-list">
            <dt>Opt-in proof URL</dt>
            <dd>{origin}/sms-consent</dd>
            <dt>Opt-in image URL</dt>
            <dd>{origin}/compliance/twilio-sms-opt-in-example.png</dd>
            <dt>Example message image URL</dt>
            <dd>{origin}/compliance/twilio-sms-example-message.png</dd>
            <dt>Signup URL</dt>
            <dd>{origin}/onboarding</dd>
            <dt>Privacy Policy</dt>
            <dd>{origin}/privacy</dd>
            <dt>Terms</dt>
            <dd>{origin}/terms</dd>
          </dl>
        </div>
      </section>

      <section className="legal-optin-demo" aria-label="SMS opt-in disclosure example">
        <div>
          <div className="section-kicker">Displayed during Chara onboarding</div>
          <h2>Branded SMS opt-in example</h2>
          <p>This is the SMS opt-in language shown to athletes before Chara sends recurring text messages.</p>
        </div>
        <div className="legal-proof-image-frame">
          <img
            src="/compliance/twilio-sms-opt-in-example.png"
            alt="Chara onboarding SMS opt-in example showing mobile phone field and unchecked consent checkbox"
          />
        </div>
      </section>

      <section className="legal-document compact">
        <section className="legal-section">
          <h2>Example SMS</h2>
          <div className="legal-proof-image-frame small">
            <img
              src="/compliance/twilio-sms-example-message.png"
              alt="Chara example SMS showing STOP opt-out and HELP instructions"
            />
          </div>
          <p>Chara: Your workout plan has been updated for this week. Today is 35 min easy with relaxed strides. Reply STOP to opt out or HELP for help.</p>
        </section>
        <section className="legal-section">
          <h2>Opt-Out and Help</h2>
          <p><strong>STOP:</strong> Users can reply STOP to opt out of SMS messages.</p>
          <p><strong>HELP:</strong> Users can reply HELP for help or contact {LEGAL_CONTACT_EMAIL}.</p>
          <p>Carriers are not liable for delayed or undelivered messages.</p>
        </section>
        <section className="legal-section">
          <h2>Mobile Data Sharing Statement</h2>
          <p>Mobile opt-in information and consent data will not be sold, rented, or shared with third parties for their marketing or promotional purposes.</p>
        </section>
      </section>
    </LegalChrome>
  );
}

export default function LegalPages() {
  const path = window.location.pathname;
  if (path.startsWith("/terms")) return <TermsPage />;
  if (path.startsWith("/sms-consent")) return <SmsConsentProofPage />;
  return <PrivacyPolicyPage />;
}
